import { type FC, type MouseEvent as ReactMouseEvent, type RefObject, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/shared/ui';
import { MAX_NAVIGATION_WIDTH, MIN_NAVIGATION_WIDTH } from '../../model';

/**
 * CSS-переменная, которой ширина меню показывается на время перетаскивания.
 *
 * Имя — собственное, а не `--sidebar-width-temp` панели копилота: переменные наследуются,
 * а сайдбар приложения лежит выше по дереву, и общее имя связало бы независимые границы.
 */
const TEMP_WIDTH_VARIABLE = '--attr-nav-width-temp';

type Props = {
  /** Элемент с CSS-переменными ширины меню — контекст сайдбара таба атрибутов. */
  sidebarContextRef: RefObject<HTMLDivElement | null>;
  /** Текущая ширина меню, px — точка отсчёта перетаскивания. */
  width: number;
  /** Итоговая ширина отпущенной границы. */
  onWidthChange: (width: number) => void;
};

/**
 * Граница между меню навигации по атрибутам и значениями: перетаскиванием задаёт ширину меню.
 *
 * Во время перетаскивания ширину показывает сам интерфейс — временная CSS-переменная
 * перекрывает сохранённую. В настройки уходит только отпущенная граница: настройки
 * синхронизируются с профилем целиком, и запись на каждое движение мыши гнала бы поток
 * запросов ради промежуточных значений, которых пользователь не выбирал.
 */
export const NavigationResizeHandle: FC<Props> = ({ sidebarContextRef, width, onWidthChange }) => {
  const { t } = useTranslation(['dialogs']);

  const handleResizeStart = useCallback(
    (event: ReactMouseEvent) => {
      event.preventDefault();
      const sidebarContext = sidebarContextRef.current;
      if (!sidebarContext) return;

      const startX = event.clientX;
      let currentWidth = width;

      const applyWidth = (moveEvent: MouseEvent) => {
        const draggedWidth = width + moveEvent.clientX - startX;
        currentWidth = Math.min(MAX_NAVIGATION_WIDTH, Math.max(MIN_NAVIGATION_WIDTH, draggedWidth));
        sidebarContext.style.setProperty(TEMP_WIDTH_VARIABLE, `${currentWidth}px`);
      };

      const stopResize = () => {
        window.removeEventListener('mousemove', applyWidth);
        window.removeEventListener('mouseup', stopResize);
        // Клик по границе без перетаскивания ширину не задавал: настройку он не заводит,
        // иначе у пользователя, ничего не менявшего, появилось бы отклонение от умолчания
        if (currentWidth !== width) onWidthChange(currentWidth);
        sidebarContext.style.removeProperty(TEMP_WIDTH_VARIABLE);
      };

      window.addEventListener('mousemove', applyWidth);
      window.addEventListener('mouseup', stopResize);
    },
    [sidebarContextRef, width, onWidthChange],
  );

  // Граница вытянута во всю высоту меню, поэтому подсказка показывается у места наведения:
  // у края она встала бы в стороне от указателя, которым на границу навели
  return (
    <Tooltip tooltip={t('dialogs:resizeAttributesNavigation')} atPointer>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('dialogs:resizeAttributesNavigation')}
        tabIndex={-1}
        className="w-1 shrink-0 cursor-col-resize hover:bg-border transition-colors"
        onMouseDown={handleResizeStart}
      />
    </Tooltip>
  );
};
